/**
 * The ONE web path to Invygo's own booking id of a contract (owner, 28 Sep 2026: the Invygo sheet is no longer
 * uploaded - "everything from Ejar").
 *
 * Where the id comes from (verified 28 Sep 2026):
 * - a new Invygo contract ("B" number at the Invygo branch) -> its Ejar booking -> the booking's
 *   brokerBookingReferenceNumber (the engine keeps it as ejar_bookings.broker_ref), e.g. 441738;
 * - an old I / C Business Bay contract -> the old Invygo sheet's cars (history, read only), by agreement.
 * Ejar's own booking number (B26000D04442492) is NEVER an Invygo id.
 *
 * The engine answers both (POST /api/store/contracts/invygo-ids, GET /api/store/contracts/by-invygo/{id}); those
 * routes need only the contracts store (FINES_PG_DSN), which live has with the flags off too - so they are read in
 * BOTH modes. With the flags off the old sheet was the only other way a page got an Invygo id, and it holds none of
 * the B contracts (every Invygo contract since 16 Sep 2026): reading the store only under V2 left every new contract
 * without its id until the switch (review, 28 Sep 2026). With the flags off the Firestore history still fills the
 * contracts the engine has no id for (an old contract the store's copy of the history may not hold yet), and it is
 * the whole answer while the engine has no store (503 "contracts store not configured") or no such route (404, an
 * older engine). Any other failure is a failed read and throws - an empty map would read as "no Invygo id".
 */
import { finesService } from './finesService';
import { USE_CONTRACTS_V2 } from './contractsV2';
import { invygoService } from './firestore';

/** The engine reads at most this many contract numbers a call (store_routes MAX_IDS). */
export const INVYGO_IDS_MAX = 1000;
export const INVYGO_FROM_BOOKING = 'ejar booking';
export const INVYGO_FROM_HISTORY = 'invygo history';

const key = (v) => String(v ?? '').trim().toUpperCase();
const text = (v) => (v === null || v === undefined ? '' : String(v).trim());

/** Contract numbers as the engine keys them: trimmed, upper case, once each, blanks left out. */
export const contractKeysOf = (contractNos) => [...new Set((contractNos || []).map(key).filter(Boolean))];

/**
 * The engine cannot answer from a store at all: none configured (503 "contracts store not configured" - locally, or
 * an engine without FINES_PG_DSN) or no such route (404 - an older engine). Only then does the flags-off web read the
 * Firestore history alone, as it did before the store; "contracts store unavailable" (the database is down), a
 * refusal or no answer is a failed read.
 */
export const storeAbsent = (err) => {
    const status = err?.response?.status;
    const detail = String(err?.response?.data?.detail || '');
    return status === 404 || (status === 503 && /not configured/i.test(detail));
};

/** The engine's Invygo ids of these contract numbers, in chunks of INVYGO_IDS_MAX, into `out`. Throws on a failure. */
const engineIdsInto = async (wanted, out) => {
    for (let i = 0; i < wanted.length; i += INVYGO_IDS_MAX) {
        const answer = await finesService.storeInvygoIds(wanted.slice(i, i + INVYGO_IDS_MAX));
        Object.entries((answer && answer.items) || {}).forEach(([contractNo, item]) => {
            const invygoId = text(item && item.invygoId);
            if (!invygoId) return;
            out.set(key(contractNo), {
                invygoId,
                source: text(item.source) || INVYGO_FROM_BOOKING,
                bookingNumber: text(item.bookingNumber)
            });
        });
    }
};

/** The old Invygo sheet's history (Firestore invygo_fleet), by agreement, for the asked numbers `out` lacks. */
const historyIdsInto = async (wanted, out) => {
    const asked = new Set(wanted.filter((no) => !out.has(no)));
    if (asked.size === 0) return;
    const cars = (await invygoService.getAllInvygoCars()) || [];
    cars.forEach((car) => {
        const agreement = key(car && car.agreement);
        if (!agreement || !asked.has(agreement) || out.has(agreement)) return;
        const invygoId = text(car.booking_id) || text(car.id);
        if (!invygoId) return;
        out.set(agreement, {
            invygoId,
            source: INVYGO_FROM_HISTORY,
            customerName: text(car.customer_name),
            phone: text(car.customer_phone)
        });
    });
};

/**
 * Map(CONTRACT NO -> { invygoId, source, bookingNumber?, customerName?, phone? }) of the contracts that have an Invygo
 * id; a contract with none is not in the map. A failed read THROWS - an empty map would read as "no Invygo id" on
 * every row. V2: the engine only (the store holds the history too). Flags off: the engine, then the Firestore history
 * for the numbers it had no id for (the history alone when the engine has no store - storeAbsent).
 */
export async function invygoIdsFor(contractNos) {
    const wanted = contractKeysOf(contractNos);
    const out = new Map();
    if (wanted.length === 0) return out;
    if (USE_CONTRACTS_V2) {
        await engineIdsInto(wanted, out);
        return out;
    }
    try {
        await engineIdsInto(wanted, out);
    } catch (err) {
        if (!storeAbsent(err)) throw err;
        out.clear();
    }
    await historyIdsInto(wanted, out);
    return out;
}

/**
 * The old Invygo sheet's history ONLY, by agreement (flags off): its customer names for a whole E-Check history
 * (Multi-Car's System Analysis reads thousands of contracts - asking the engine for all of them only to take the
 * sheet's names would be many calls for nothing). Never the source of an Invygo id on a page: that is invygoIdsFor.
 */
export async function invygoHistoryFor(contractNos) {
    const out = new Map();
    const wanted = contractKeysOf(contractNos);
    if (wanted.length) await historyIdsInto(wanted, out);
    return out;
}

/**
 * The contract numbers of one Invygo booking id (upper case, once each) - [] when none is known yet (a booking with
 * no contract yet, or an unknown id). A failed read throws. V2: the engine. Flags off: the engine's contracts and the
 * history's agreement (the history alone when the engine has no store - storeAbsent).
 */
export async function contractsForInvygoId(id) {
    const wanted = text(id);
    if (!wanted) return [];
    if (USE_CONTRACTS_V2) {
        const answer = await finesService.storeByInvygoId(wanted);
        return contractKeysOf(((answer && answer.items) || []).map((item) => item && item.contractNo));
    }
    let fromEngine = [];
    try {
        const answer = await finesService.storeByInvygoId(wanted);
        fromEngine = ((answer && answer.items) || []).map((item) => item && item.contractNo);
    } catch (err) {
        if (!storeAbsent(err)) throw err;
    }
    const car = await invygoService.getInvygoDataByIdentifier(wanted);
    return contractKeysOf([...fromEngine, car && car.agreement]);
}
